/**
 * Used against a ceiling.
 *
 * The daily and weekly ceilings are the centre of this product, so the bar has
 * to be honest about all three numbers: what has already gone out, what is
 * reserved but not yet done, and what is left. Reserved work still counts
 * against the ceiling, so it is drawn as a distinct hatched segment rather than
 * quietly folded into either side.
 */
import type { ReactNode } from 'react'
import { cn } from '../lib/cn'
import { formatNumber, ratio } from '../lib/format'
import type { Tone } from '../lib/labels'
import { tone as toneOf } from '../lib/labels'

export const TONE_FILL: Record<Tone, string> = {
  neutral: 'bg-ink-subtle',
  info: 'bg-info',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  accent: 'bg-accent',
}

export interface MeterProps {
  /** Actions already committed. */
  value: number
  /** The ceiling. */
  max: number
  /** Reservations still holding a slot, drawn between used and remaining. */
  pending?: number
  label?: ReactNode
  /** Small text under the bar, such as when the ceiling resets. */
  caption?: ReactNode
  /** Forces a tone. By default the bar colours itself by how close it is. */
  tone?: Tone
  size?: 'sm' | 'md'
  /** Shows "4 of 20 used" beside the label. */
  showNumbers?: boolean
  className?: string
  /**
   * `usage` is the settings usage row as measured: an 8 tall bar with a 12px
   * thumb at the end of the fill, and under it the number at 15/22 500 with
   * its label at 13/18. `default` keeps the label and the numbers above the
   * bar and what is reserved and left under it.
   */
  layout?: 'default' | 'usage'
  /**
   * Link buttons in a pill centred over the bar, such as `Raise the limit`.
   * Usage layout only.
   */
  actions?: ReactNode
  /**
   * Writes every number the meter prints, and the one it reads out. Counts by
   * default. Pass a currency formatter for a budget, so the bar says `$40 /
   * $100` rather than two bare numbers somebody has to know are dollars.
   */
  formatValue?: (value: number) => string
  /**
   * A mark across the bar at this fraction of the ceiling, such as the point
   * an alert fires. Drawn as a 2px rule a little taller than the track, in
   * the strong line colour, so it reads against an empty track and a full one.
   * Decorative: say what it marks in the caption.
   */
  threshold?: number
  /** The bar's accessible name, when nothing beside it already names it. */
  name?: string
}

/** Where the threshold mark sits, clamped to the bar, or null when there is none. */
export function thresholdLeft(threshold: number | undefined): string | null {
  if (threshold === undefined || !Number.isFinite(threshold)) return null
  const at = Math.min(1, Math.max(0, threshold))
  return `calc(${at * 100}% - 1px)`
}

function ThresholdMark({ threshold }: { threshold: number | undefined }) {
  const left = thresholdLeft(threshold)
  if (left === null) return null
  return (
    <span
      aria-hidden="true"
      data-meter-threshold=""
      className="pointer-events-none absolute -bottom-1 -top-1 w-[2px] rounded-full bg-line-strong"
      style={{ left }}
    />
  )
}

export function Meter({
  value,
  max,
  pending = 0,
  label,
  caption,
  tone,
  size = 'md',
  showNumbers = true,
  className,
  layout = 'default',
  actions,
  formatValue = formatNumber,
  threshold,
  name,
}: MeterProps) {
  const usedRatio = ratio(value, max)
  const pendingRatio = ratio(value + pending, max) - usedRatio
  const resolvedTone = tone ?? toneOf.usage(ratio(value + pending, max))
  const remaining = Math.max(0, max - value - pending)
  const height = size === 'sm' ? 'h-1.5' : 'h-2'
  const valueText = `${formatValue(value + pending)} of ${formatValue(max)} used, ${formatValue(remaining)} left`

  if (layout === 'usage') {
    const filled = ratio(value + pending, max)
    return (
      <div className={cn('space-y-2', className)} data-numeric="">
        <div className="relative">
          <div
            className="flex h-2 w-full overflow-hidden rounded-full bg-surface-selected"
            role="meter"
            aria-label={name}
            aria-valuenow={value + pending}
            aria-valuemin={0}
            aria-valuemax={max}
            aria-valuetext={valueText}
          >
            <div
              className={cn('h-full rounded-full transition-[width] duration-500', TONE_FILL[resolvedTone])}
              style={{ width: `${filled * 100}%` }}
            />
          </div>
          {/* The thumb sits on the fill's end, outside the clipped track so
              its ring is not cut in half. */}
          <span
            aria-hidden="true"
            data-meter-thumb=""
            className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-primary-600 bg-surface"
            style={{ left: `${filled * 100}%` }}
          />
          <ThresholdMark threshold={threshold} />
          {actions ? (
            <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-3 rounded-full border border-primary-200 bg-surface px-3 py-0.5 text-sm font-semibold">
              {actions}
            </div>
          ) : null}
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-medium tabular-nums text-ink">
            {formatValue(value + pending)} / {formatValue(max)}
          </span>
          {label ? <span className="text-sm text-ink-subtle">{label}</span> : null}
        </div>
        {caption ? <p className="text-sm text-ink-subtle">{caption}</p> : null}
      </div>
    )
  }

  return (
    <div className={cn('space-y-1.5', className)}>
      {(label || showNumbers) && (
        <div className="flex items-baseline justify-between gap-3">
          {label ? <span className="text-xs font-medium text-ink-muted">{label}</span> : <span />}
          {showNumbers ? (
            <span className="text-xs tabular-nums text-ink-muted">
              <span className="font-semibold text-ink">{formatValue(value + pending)}</span> of{' '}
              {formatValue(max)}
            </span>
          ) : null}
        </div>
      )}

      <div className="relative">
        <div
          className={cn('flex w-full overflow-hidden rounded-full bg-surface-selected', height)}
          role="meter"
          aria-label={name}
          aria-valuenow={value + pending}
          aria-valuemin={0}
          aria-valuemax={max}
          aria-valuetext={valueText}
        >
          <div
            className={cn('h-full transition-[width] duration-500', TONE_FILL[resolvedTone])}
            style={{ width: `${usedRatio * 100}%` }}
          />
          {/*
            The hatch is the track showing through, so it is cut in the track's
            own token (`surface-selected`, the measured track) and the segment
            keeps its tone at full strength.

            It used to be white stripes over a fill held at 45 percent opacity,
            and white is not a token: it cannot follow the theme, and at that
            opacity it had almost nothing left to lighten. Measured stripe against
            gap, the old recipe ran from 1.23 to 1 on a light warning bar to 1.44
            on a dark accent one, which is not a hatch, it is a faintly cloudy
            bar. Gaps cut in the solid tone read from 2.7 to 7.87 across the same
            six cases, and it is what the dashboard's stacked meter already does.
          */}
          {pending > 0 ? (
            <div
              className={cn('h-full transition-[width] duration-500', TONE_FILL[resolvedTone])}
              style={{
                width: `${pendingRatio * 100}%`,
                backgroundImage:
                  'repeating-linear-gradient(45deg, rgb(var(--c-surface-selected)) 0 2px, transparent 2px 5px)',
              }}
            />
          ) : null}
        </div>
        <ThresholdMark threshold={threshold} />
      </div>

      <div className="flex items-baseline justify-between gap-3 text-2xs text-ink-subtle">
        <span>
          {pending > 0 ? `${formatValue(pending)} reserved and not done yet` : null}
        </span>
        <span>{formatValue(remaining)} left</span>
      </div>

      {caption ? <p className="text-2xs text-ink-subtle">{caption}</p> : null}
    </div>
  )
}

/** A single slim bar with no labels, for inside a dense table cell. */
export function MiniMeter({
  value,
  max,
  className,
  title,
}: {
  value: number
  max: number
  className?: string
  title?: string
}) {
  const filled = ratio(value, max)
  const resolvedTone = toneOf.usage(filled)
  return (
    <span
      className={cn('inline-flex h-1.5 w-16 overflow-hidden rounded-full bg-surface-selected align-middle', className)}
      title={title ?? `${formatNumber(value)} of ${formatNumber(max)}`}
      role="img"
      aria-label={title ?? `${formatNumber(value)} of ${formatNumber(max)} used`}
    >
      <span className={cn('h-full', TONE_FILL[resolvedTone])} style={{ width: `${filled * 100}%` }} />
    </span>
  )
}

/** The background class for a tone, for anything that draws its own bar or dot. */
export function toneBar(tone: Tone): string {
  return TONE_FILL[tone]
}

export interface ProgressBarProps {
  value: number
  max?: number
  tone?: Tone
  /** Accessible name. A bar with no name is a decoration. */
  label?: string
  className?: string
  /** For work that is running with no known end, such as an upload with no length. */
  indeterminate?: boolean
}

/**
 * A plain bar for progress through a known amount of work. `Meter` is the one
 * to use against a ceiling: it also draws what is reserved and what is left.
 */
export function ProgressBar({
  value,
  max = 1,
  tone = 'accent',
  label,
  className,
  indeterminate = false,
}: ProgressBarProps) {
  const filled = ratio(value, max)
  return (
    <div
      className={cn('h-1.5 w-full overflow-hidden rounded-full bg-surface-selected', className)}
      role="progressbar"
      aria-label={label}
      aria-valuenow={indeterminate ? undefined : Math.round(filled * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      {indeterminate ? (
        <div className={cn('h-full w-1/4 animate-indeterminate-bar rounded-full', TONE_FILL[tone])} />
      ) : (
        <div
          className={cn('h-full rounded-full transition-[width] duration-500', TONE_FILL[tone])}
          style={{ width: `${filled * 100}%` }}
        />
      )}
    </div>
  )
}

export interface StackSegment {
  key: string
  value: number
  tone: Tone
  label: string
}

/**
 * One bar made of labelled parts, for a mix rather than a proportion of a
 * ceiling: what each component contributed to a score, or how a campaign's
 * enrollments are spread across states.
 */
export function StackedBar({
  segments,
  className,
  height = 'h-2',
}: {
  segments: StackSegment[]
  className?: string
  height?: string
}) {
  const total = segments.reduce((sum, segment) => sum + Math.max(0, segment.value), 0)
  if (total <= 0) {
    return <div className={cn(height, 'w-full rounded-full bg-surface-selected', className)} />
  }
  return (
    <div className={cn('flex w-full overflow-hidden rounded-full bg-surface-selected', height, className)}>
      {segments
        .filter((segment) => segment.value > 0)
        .map((segment) => (
          <div
            key={segment.key}
            className={TONE_FILL[segment.tone]}
            style={{ width: `${(segment.value / total) * 100}%` }}
            title={`${segment.label}: ${Math.round((segment.value / total) * 100)}%`}
          />
        ))}
    </div>
  )
}

export { GAUGE_STATE_COPY, Gauge, MiniSlider, arcLength, gaugeState } from './Gauge'
export type { GaugeProps, GaugeState, MiniSliderProps } from './Gauge'
